// src/types/role.types.ts

/**
 * Permission interface
 * Representa un permiso granular del sistema (cuando se recibe del backend)
 */
export interface Permission {
  id: string;
  module: string; // "products", "inventory", "warehouse", "users", "roles", etc.
  menu: string; // "catalog", "register", "query", "users", "roles", etc.
  action: string; // "view", "add", "edit", "delete", "export", "import", "print", "cancel", "transfer", "block", "assign"
  resource?: string; // Opcional: "menu", "button", "action"
}

/**
 * CreatePermissionDto interface
 * DTO para crear un permiso (sin id, el backend lo genera)
 */
export interface CreatePermissionDto {
  module: string;
  menu: string;
  action: string;
  resource?: string;
}

/**
 * Role interface
 * Extiende el modelo de API con campos adicionales necesarios para el frontend
 */
export interface Role {
  id: string;
  name: string;
  description?: string;

  // Estado
  isActive: boolean; // true = activo, false = bloqueado
  isSystem: boolean; // true = rol del sistema (protegido)

  // Estadísticas
  userCount: number; // Número de usuarios con este rol

  // Metadatos
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  lastModifiedBy?: string; // ID del usuario que modificó

  // Campos de bloqueo (opcionales, solo si está bloqueado)
  blockedAt?: string; // ISO date string
  blockedBy?: string; // ID del usuario que bloqueó
  blockReason?: string; // Razón del bloqueo (opcional)

  // Permisos (se cargarán en el detalle, no siempre presentes)
  permissions?: Permission[];
}

/**
 * RoleFilter interface
 * Filtros para búsqueda y filtrado de roles
 */
export interface RoleFilter {
  searchTerm: string; // Búsqueda en nombre y descripción
  status: 'all' | 'active' | 'blocked'; // Filtro por estado
  startDate?: string; // Fecha de creación inicio (ISO date string)
  endDate?: string; // Fecha de creación fin (ISO date string)
  sortBy?: 'name' | 'createdAt' | 'userCount'; // Campo de ordenamiento (siempre ascendente)
}

/**
 * RolePaginationState interface
 * Estado de paginación para la tabla de roles
 */
export interface RolePaginationState {
  itemsPerPage: number;
  currentPage: number;
  totalItems: number;
}

/**
 * ApiRole interface
 * Modelo de rol tal como viene del API (puede no tener todos los campos)
 */
export interface ApiRole {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  // Campos adicionales que pueden venir del backend
  isActive?: boolean;
  isSystem?: boolean;
  userCount?: number;
  lastModifiedBy?: string;
  blockedAt?: string;
  blockedBy?: string;
  blockReason?: string;
}

/**
 * ApiResponse interface for Roles
 * Respuesta paginada del API
 */
export interface RoleApiResponse {
  data: ApiRole[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * CreateRoleDto interface
 * DTO para crear un nuevo rol
 */
export interface CreateRoleDto {
  name: string;
  description?: string;
  permissions?: CreatePermissionDto[];
}

/**
 * UpdateRoleDto interface
 * DTO para actualizar un rol existente
 */
export interface UpdateRoleDto {
  name?: string;
  description?: string;
  isActive?: boolean;
  permissions?: CreatePermissionDto[];
}

/**
 * BlockRoleDto interface
 * DTO para bloquear un rol
 */
export interface BlockRoleDto {
  reason?: string; // Razón opcional del bloqueo
}

/**
 * RoleUser interface
 * Usuario asignado a un rol (para mostrar en detalles)
 */
export interface RoleUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  fullName?: string; // Computado: firstName + lastName
}

/**
 * RoleDetail interface
 * Información completa de un rol para el modal de detalles
 */
export interface RoleDetail extends Role {
  users: RoleUser[]; // Lista de usuarios asignados
  permissions: Permission[]; // Lista completa de permisos
}

/**
 * SystemRoleNames
 * Nombres de roles del sistema que están protegidos
 */
export const SYSTEM_ROLE_NAMES = ['Super Admin', 'Admin', 'System'] as const;

export type SystemRoleName = (typeof SYSTEM_ROLE_NAMES)[number];

/**
 * Helper type guard para verificar si un rol es del sistema
 */
export function isSystemRole(role: Role | ApiRole): boolean {
  return (
    SYSTEM_ROLE_NAMES.some((name) => role.name.toLowerCase() === name.toLowerCase()) ||
    (role as Role).isSystem === true
  );
}

/**
 * Helper para mapear ApiRole a Role
 */
export function mapApiRoleToRole(apiRole: ApiRole, userCount: number = 0): Role {
  return {
    id: apiRole.id,
    name: apiRole.name,
    description: apiRole.description,
    isActive: apiRole.isActive ?? true,
    isSystem: apiRole.isSystem ?? isSystemRole(apiRole),
    userCount: apiRole.userCount ?? userCount,
    createdAt: apiRole.createdAt,
    updatedAt: apiRole.updatedAt,
    lastModifiedBy: apiRole.lastModifiedBy,
    blockedAt: apiRole.blockedAt,
    blockedBy: apiRole.blockedBy,
    blockReason: apiRole.blockReason,
  };
}

/**
 * RoleWizardPermissions interface
 * Estructura de permisos para el wizard de creación de roles
 */
export interface RoleWizardPermissions {
  [moduleId: string]: {
    enabled: boolean;
    menus: {
      [menuId: string]: {
        visible: boolean;
        mainButtons: {
          [buttonId: string]: boolean;
        };
        rowActions: {
          [actionId: string]: boolean;
        };
        columnFlags?: {
          [flagId: string]: boolean;
        };
        salesFlowActions?: {
          [actionId: string]: boolean;
        };
      };
    };
  };
}

/**
 * RoleWizardData interface
 * Datos completos del wizard de creación de roles
 */
export interface RoleWizardData {
  // Paso 1: Información básica
  name: string;
  description: string;

  // Paso 2: Permisos
  permissions: RoleWizardPermissions;
}
